import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  requestGetBusinessServiceCategory,
  requestListServiceByCategoryName,
  useBusinessServiceDispatch,
  useBusinessServiceState,
} from "../../Context";
import { useNavigate, useParams } from "react-router-dom";
import { useTitle } from "../../Hooks/useTitle";
import { Alert } from "../../Components/Alert";
import { CheckIcon } from "@heroicons/react/outline";
import { NumericFormat } from "react-number-format";
import { EmptyList } from "../../Components/EmptyList";
import Badge from "../../Components/Badge";

const ManageServiceDetailList = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const dispatch = useBusinessServiceDispatch();
  const {
    businessServiceCategory,
    serviceCategoryLoading,
    serviceCategoryError,
    businessServices,
    businessServicesLoading,
    businessServicesError,
  } = useBusinessServiceState();

  useTitle(
    t(
      businessServiceCategory?.name
        ? `Kelola Layanan ${businessServiceCategory?.name}`
        : `Kelola Layanan Usaha`
    )
  );

  useEffect(() => {
    requestGetBusinessServiceCategory(dispatch, { id });
  }, [dispatch, id]);

  useEffect(() => {
    if (businessServiceCategory?.id) {
      requestListServiceByCategoryName(dispatch, {
        categoryID: businessServiceCategory.id,
      });
    }
  }, [businessServiceCategory?.id, dispatch]);

  const BusinessServiceSkeleton = ({ length }) => {
    return (
      <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8">
        {Array.from({ length }).map((_, index) => (
          <div
            key={index}
            className="relative p-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col animate-pulse"
          >
            <div className="flex-1">
              <div className="h-4 w-[50%] mt-2 mb-1 bg-gray-300 rounded-md"></div>
              <div className="mt-4 flex items-baseline">
                <div className="h-6 w-[50%] mt-2 mb-1 mr-2 bg-gray-300 rounded-md"></div>
                <div className="h-6 w-[25%] mt-2 mb-1 bg-gray-300 rounded-md"></div>
              </div>
              <div className="h-3 w-full mt-8 mb-1 bg-gray-300 rounded-md"></div>
              <div className="h-3 w-[50%] mt-2 mb-1 bg-gray-300 rounded-md"></div>
              <div className="mt-10">
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
                <div className="h-3 w-full mt-4 mb-1 bg-gray-300 rounded-md"></div>
              </div>
            </div>
            <div className="mt-10">
              <div className="h-12 w-full mt-2 bg-gray-300 rounded-md"></div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-3xl lg:max-w-7xl">
      <div className="max-w-7xl mx-auto py-6">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <h1 className="text-xl font-bold text-gray-900">
              {serviceCategoryLoading || businessServicesLoading ? (
                <div className="h-6 bg-gray-300 rounded w-3/4 animate-pulse"></div>
              ) : businessServicesError || serviceCategoryError ? (
                t("Kelola Layanan Perizinan Usaha")
              ) : businessServiceCategory?.name ? (
                t(`Kelola Layanan ${businessServiceCategory.name}`)
              ) : (
                t("Kelola Layanan Perizinan Usaha")
              )}
            </h1>
          </div>
        </dl>
      </div>
      {serviceCategoryLoading || businessServicesLoading ? (
        <BusinessServiceSkeleton length={3} />
      ) : businessServicesError || serviceCategoryError ? (
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            message={
              businessServicesError?.message || serviceCategoryError?.message
            }
            errors={
              businessServicesError?.errors || serviceCategoryError?.errors
            }
          />
        </div>
      ) : (
        <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8 lg:gap-y-8">
          {businessServices.length > 0 ? (
            businessServices.map((businessService) => {
              const statusDetails =
                businessService.status === "AVAILABLE"
                  ? { label: t("available"), type: "success" }
                  : { label: t("unavailable"), type: "secondary" };
              return (
                <div
                  key={businessService.id}
                  className="relative p-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col"
                >
                  <div className="flex-1">
                    <Badge
                      title={statusDetails.label}
                      type={statusDetails.type}
                    />
                    <h3 className="mt-2 text-xl font-semibold text-gray-900">
                      {businessService.name}
                    </h3>
                    <p className="mt-4 flex items-baseline text-gray-900">
                      <span className="text-3xl font-extrabold tracking-tight flex gap-2">
                        <span className="font-normal">Mulai</span>
                        <NumericFormat
                          displayType={"text"}
                          value={businessService.price}
                          thousandSeparator={"."}
                          decimalSeparator={","}
                          prefix={"Rp"}
                        />
                      </span>
                    </p>
                    <p className="mt-6 text-gray-500">
                      {businessService.description}
                    </p>

                    {/* Benefit list */}
                    <ul className="mt-6 space-y-2">
                      {businessService.benefits.map((benefit) => (
                        <li key={benefit} className="flex">
                          <CheckIcon
                            className="flex-shrink-0 w-6 h-6 text-blue-500"
                            aria-hidden="true"
                          />
                          <span className="ml-3 text-gray-500">
                            {t(benefit)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button
                    onClick={() =>
                      navigate(
                        `/manage-business-service/edit/${businessService.id}`
                      )
                    }
                    className="bg-blue-600 text-white hover:bg-blue-700 mt-8 block w-full py-3 px-6 border border-transparent rounded-md text-center font-medium disabled:bg-gray-300 disabled:cursor-not-allowed disabled:text-white"
                  >
                    <span>{t("Edit")}</span>
                  </button>
                </div>
              );
            })
          ) : (
            <div className="bg-white col-span-12 rounded-lg p-4">
              <EmptyList title="noServicesAvailable" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManageServiceDetailList;
